import React, { useState, useEffect, useRef, useMemo } from "react";
import { Plus, X, BookOpen, Edit2, Upload, Image as ImageIcon } from "lucide-react";
import Swal from "sweetalert2";
import JoditEditor from 'jodit-react';
import axios from 'axios';

// Backend API Configuration
const API_BASE_URL = "https://backend.dlapakistan.com/api/dla-trade/headline-sponsor";

const HeadlineSponsor = () => {
  const [sponsorContents, setSponsorContents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);

  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    is_active: true
  });

  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const emerald600 = "#059669";

  // Jodit Editor Configuration
  const config = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  // Axios instance
  const api = axios.create({
    baseURL: "https://backend.dlapakistan.com",
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000,
  });

  // Fetch sponsor data from backend
  const fetchSponsorData = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      
      if (response.data) {
        let data = response.data;
        
        if (Array.isArray(data)) {
          if (data.length > 0) {
            setSponsorContents(data);
            if (data[0]?.image_url) {
              const cacheBuster = `?t=${Date.now()}`;
              const imageUrl = data[0].image_url.includes('?') 
                ? data[0].image_url + '&' + cacheBuster.substring(1)
                : data[0].image_url + cacheBuster;
              setImagePreview(imageUrl);
            }
          } else {
            setSponsorContents([]);
            setImagePreview(null);
          }
        } else {
          setSponsorContents([data]);
          if (data.image_url) {
            const cacheBuster = `?t=${Date.now()}`;
            const imageUrl = data.image_url.includes('?') 
              ? data.image_url + '&' + cacheBuster.substring(1)
              : data.image_url + cacheBuster;
            setImagePreview(imageUrl);
          } else {
            setImagePreview(null);
          }
        }
      } else {
        setSponsorContents([]);
        setImagePreview(null);
      }
    } catch (error) {
      const savedData = localStorage.getItem("sponsorHeadline_Storage");
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData);
          setSponsorContents(parsedData);
          if (parsedData.length > 0 && parsedData[0].image) {
            setImagePreview(parsedData[0].image);
          }
        } catch (e) {
          console.error("Error parsing saved data:", e);
        }
      }
      
      if (error.response?.status !== 404) {
        await Swal.fire({
          icon: "warning",
          title: "Connection Issue",
          text: "Using cached data. Some features may not work properly.",
          confirmButtonColor: emerald600,
          timer: 2000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchSponsorData();
  }, []);

  const closeModal = () => {
    setShowModal(false);
    setEditingContent(null);
    setForm({ 
      title: "", 
      subtitle: "", 
      description: "", 
      is_active: true 
    });
    setImagePreview(null);
    setImageBase64(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Please upload an image file (JPG, PNG, GIF, etc.)",
        confirmButtonColor: emerald600
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image size should be less than 5MB",
        confirmButtonColor: emerald600
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setImagePreview(base64String);
      setImageBase64(base64String);
    };
    reader.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!form.title.trim() || !form.subtitle.trim() || !form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all required fields before saving.",
        confirmButtonColor: emerald600
      });
      return;
    }

    try {
      setSaving(true);

      const dataToSend = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        description: form.description.trim(),
        is_active: true
      };

      if (imageBase64) {
        dataToSend.image_base64 = imageBase64;
      } 
      else if (!imagePreview && editingContent?.image_url) {
        dataToSend.image_base64 = '';
      }

      let response;
      
      if (editingContent && editingContent.id) {
        response = await api.put(`${API_BASE_URL}/${editingContent.id}/`, dataToSend);
      } else {
        response = await api.post(API_BASE_URL + "/", dataToSend);
      }

      await Swal.fire({ 
        icon: "success", 
        title: "Saved Successfully!", 
        text: editingContent ? "Sponsor content updated." : "Sponsor content created.",
        showConfirmButton: false, 
        timer: 1500 
      });

      closeModal();
      await fetchSponsorData();

    } catch (error) {
      let errorMessage = "Failed to save content. Please try again.";
      
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (typeof errorData === 'object') {
            const errors = Object.entries(errorData)
              .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
              .join('\n');
            errorMessage = `Validation errors:\n${errors}`;
          } else {
            errorMessage = `Bad request: ${errorData}`;
          }
        } else if (error.response.status === 401) {
          errorMessage = "Unauthorized. Please check if you need authentication.";
        } else if (error.response.status === 500) {
          errorMessage = "Server error. Please try again later.";
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
        
        const newContent = {
          id: Date.now().toString(),
          title: form.title.trim(),
          subtitle: form.subtitle.trim(),
          description: form.description.trim(),
          image_url: imagePreview,
          is_active: true,
          updated_at: new Date().toISOString(),
          created_at: new Date().toISOString()
        };
        
        const updatedContents = [newContent];
        setSponsorContents(updatedContents);
        localStorage.setItem("sponsorHeadline_Storage", JSON.stringify(updatedContents));
        
        if (imagePreview) {
          setImagePreview(imagePreview);
        }
        
        await Swal.fire({ 
          icon: "success", 
          title: "Saved Locally!", 
          text: "Content saved to local storage (backend offline).",
          showConfirmButton: false, 
          timer: 1500 
        });
        
        closeModal();
        return;
      }
      
      await Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: errorMessage,
        confirmButtonColor: emerald600
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    if (sponsorContents.length > 0) {
      const content = sponsorContents[0];
      setEditingContent(content);
      setForm({
        title: content.title || "",
        subtitle: content.subtitle || "",
        description: content.description || "",
        is_active: true
      });
      
      if (content.image_url) {
        const cacheBuster = `?t=${Date.now()}`;
        const imageUrl = content.image_url.includes('?') 
          ? content.image_url + '&' + cacheBuster.substring(1)
          : content.image_url + cacheBuster;
        setImagePreview(imageUrl);
      } else {
        setImagePreview(null);
      }
      
      setImageBase64(null);
      setShowModal(true);
    }
  };

  const handleEditorChange = (newContent) => {
    setForm(prev => ({
      ...prev,
      description: newContent
    }));
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-20 min-h-screen w-full">
      
      {/* HEADER - ALIGNED TO LEFT */}
      <div className="px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mt-12 md:mt-0">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">DLA Headline Sponsor</h1>
            <p className="text-gray-600 mt-1">Manage headline sponsor information and branding</p>
          </div>
          <button
            onClick={sponsorContents.length === 0 ? () => setShowModal(true) : handleEdit}
            style={{ backgroundColor: emerald600 }}
            className="flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md hover:opacity-90 transition-all"
          >
            {sponsorContents.length === 0 ? (
              <>
                <Plus className="w-5 h-5 mr-2" /> Add Sponsor Content
              </>
            ) : (
              <>
                <Edit2 className="w-4 h-4 mr-2" /> Edit Sponsor
              </>
            )}
          </button>
        </div>
      </div>

      {/* CONTENT AREA - ALL CONTENT ALIGNED TO LEFT */}
      <div className="px-8 md:px-12 mt-8 w-full pb-20">
        {sponsorContents.length === 0 ? (
          <div className="text-center py-24 bg-transparent border-2 border-dashed border-gray-300 rounded-3xl">
            <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-400 text-lg mb-2">No sponsor content added yet.</p>
            <p className="text-gray-400 text-sm">Click "Add Sponsor Content" to get started</p>
          </div>
        ) : (
          <div className="animate-fadeIn w-full">
            
            {/* TITLE - LEFT ALIGNED */}
            <h2 className="text-4xl font-bold text-gray-900 mb-8">
              {sponsorContents[0]?.title || "Headline Sponsor"}
            </h2>
            
            {/* IMAGE AND SUBTITLE LAYOUT - LEFT ALIGNED */}
            <div className="flex flex-col lg:flex-row gap-10 mb-12 items-start">
              {/* IMAGE - Displayed on left side */}
              {sponsorContents[0]?.image_url && (
                <div className="lg:w-2/5">
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <img 
                      src={`${sponsorContents[0].image_url}?t=${Date.now()}`}
                      alt="Sponsor" 
                      className="w-full h-auto object-contain"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                        e.target.alt = "Image not available";
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* SUBTITLE - Left aligned text */}
              <div className={`${sponsorContents[0]?.image_url ? 'lg:w-3/5' : 'w-full'}`}>
                <p className="text-gray-700 text-xl leading-relaxed">
                  {sponsorContents[0]?.subtitle || "No subtitle provided"}
                </p>
              </div>
            </div>

            {/* DESCRIPTION - Left aligned HTML output */}
            <div className="mt-12 w-full">
              <div 
                className="text-gray-700 leading-relaxed custom-description w-full text-left" 
                dangerouslySetInnerHTML={{ __html: sponsorContents[0]?.description || "" }} 
              />
            </div>
          </div>
        )}
      </div>

      {/* MODAL - No changes needed */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto overflow-hidden animate-modalIn border border-gray-100">
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">
                  {editingContent ? "Update Sponsor" : "Add Headline Sponsor"}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                
                {/* TITLE */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Sponsor Title/Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    value={form.title} 
                    onChange={(e) => setForm({...form, title: e.target.value})} 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all" 
                    placeholder="e.g., Our Premier Partner" 
                    required 
                    disabled={saving}
                  />
                </div>
                
                {/* SUBTITLE */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Tagline/Subtitle <span className="text-red-500">*</span>
                  </label>
                  <textarea 
                    value={form.subtitle} 
                    onChange={(e) => setForm({...form, subtitle: e.target.value})} 
                    rows="3" 
                    className="w-full px-5 py-3 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all resize-none" 
                    placeholder="Brief description or tagline for the sponsor"
                    required 
                    disabled={saving}
                  />
                </div>
                
                {/* IMAGE UPLOAD */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Sponsor Image/Logo
                  </label>
                  
                  {/* Image Preview */}
                  {imagePreview && (
                    <div className="mb-4 p-4 border-2 border-dashed border-emerald-200 rounded-2xl bg-emerald-50/50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-emerald-700">Image Preview</span>
                        <button
                          type="button"
                          onClick={removeImage}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove Image
                        </button>
                      </div>
                      <div className="flex justify-center">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-48 rounded-lg object-contain"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Upload Area */}
                  <div 
                    onClick={triggerFileInput}
                    className={`border-2 border-dashed ${imagePreview ? 'border-gray-300' : 'border-emerald-300'} rounded-2xl p-8 text-center cursor-pointer transition-all hover:bg-gray-50/50`}
                  >
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="hidden"
                      disabled={saving}
                    />
                    <Upload className={`w-12 h-12 mx-auto mb-4 ${imagePreview ? 'text-gray-400' : 'text-emerald-500'}`} />
                    <p className="text-gray-700 font-medium mb-1">
                      {imagePreview ? 'Change Image' : 'Upload Sponsor Image/Logo'}
                    </p>
                    <p className="text-gray-500 text-sm">
                      Click to upload (Max 5MB)
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      PNG, JPG, GIF, SVG recommended
                    </p>
                  </div>
                </div>
                
                {/* DESCRIPTION */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Detailed Description <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden jodit-editor-container">
                    <JoditEditor
                      ref={editorRef}
                      value={form.description}
                      config={config}
                      onChange={handleEditorChange}
                      onBlur={(newContent) => {
                        if (newContent !== form.description) {
                          setForm(prev => ({...prev, description: newContent}));
                        }
                      }}
                    />
                  </div>
                </div>
                
                {/* SUBMIT BUTTON */}
                <button 
                  type="submit" 
                  style={{ backgroundColor: emerald600 }} 
                  className={`w-full py-4 text-white rounded-2xl font-bold shadow-xl hover:brightness-110 transition-all text-lg flex items-center justify-center ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : editingContent ? (
                    'Update Sponsor'
                  ) : (
                    'Create Sponsor Content'
                  )}
                </button>
              </form>
            </div>
          </div>
        </>
      )}

      {/* STYLES */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        
        .animate-fadeIn { 
          animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        .animate-modalIn { 
          animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
        }
        
        /* Clean, simple text styling - LEFT ALIGNED */
        .custom-description * { 
          background-color: transparent !important; 
          border: none !important; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description {
          text-align: left;
        }
        
        .custom-description p {
          font-size: 1.125rem;
          line-height: 1.75;
          color: #374151;
          text-align: left;
        }
        
        .custom-description h1, 
        .custom-description h2, 
        .custom-description h3, 
        .custom-description h4, 
        .custom-description h5, 
        .custom-description h6 {
          font-weight: bold;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          color: #111827;
          text-align: left;
        }
        
        .custom-description h1 { font-size: 2.25rem; }
        .custom-description h2 { font-size: 1.875rem; }
        .custom-description h3 { font-size: 1.5rem; }
        .custom-description h4 { font-size: 1.25rem; }
        .custom-description h5 { font-size: 1.125rem; }
        .custom-description h6 { font-size: 1rem; }
        
        .custom-description ul { 
          list-style-type: disc; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description ol { 
          list-style-type: decimal; 
          padding-left: 1.5rem; 
          margin-bottom: 1rem;
          text-align: left;
        }
        
        .custom-description li {
          margin-bottom: 0.5rem;
          text-align: left;
        }
        
        .custom-description strong, 
        .custom-description b {
          font-weight: bold;
        }
        
        .custom-description em, 
        .custom-description i {
          font-style: italic;
        }
        
        .custom-description u {
          text-decoration: underline;
        }
        
        .custom-description a {
          color: #059669;
          text-decoration: underline;
        }
        
        .custom-description a:hover {
          color: #047857;
        }
        
        .custom-description blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1rem;
          margin: 1.5rem 0;
          font-style: italic;
          color: #6b7280;
          text-align: left;
        }
        
        .custom-description img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        
        .custom-description table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
          text-align: left;
        }
        
        .custom-description table th,
        .custom-description table td {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          text-align: left;
        }
        
        .custom-description table th {
          background-color: #f9fafb;
          font-weight: bold;
        }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
          border-radius: 16px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-radius: 16px 16px 0 0;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 20px !important;
          min-height: 200px;
          border-radius: 0 0 16px 16px;
        }
      `}</style>
    </div>
  );
};

export default HeadlineSponsor;